"use client";

import * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { Sheet, SheetClose, SheetPopup } from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import { useBeginLayoutSettle } from "../../hooks/use-layout-settle";
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion";
import { useAppShell } from "./app-shell-context";
import {
  APP_SHELL_ISLANDS_INSET_VARS,
  AppShellDockedIsland,
  AppShellMobileIslandLayer,
  ISLAND_HANDLE_LANE,
  PANE_TRANSITION,
  usePublishIslandsInsets,
} from "./app-shell-islands";

export { APP_SHELL_ISLANDS_INSET_VARS };
import type { Omittable } from "../../lib/props";

/**
 * The application shell: an optional full-width site header pinned to the top,
 * and below it a row of three columns — a fixed module rail, a
 * collapsible/resizable module-menu pane, and the content area rendered as an
 * inset card. Omit `siteHeader` for a shell whose navigation lives entirely in
 * the rail; the column row then owns the top inset itself.
 *
 * The shell steps down the width ladder (see `useShellTier`) in two moves
 * instead of one cliff:
 *
 *  - FULL: the header spans the whole width; the rail is always visible;
 *    collapsing (`open=false`) clips the pane to zero and slides it away,
 *    leaving just the rail. A clipped pane or island is also `inert`: its
 *    controls leave the tab order and the accessibility tree the moment it
 *    closes, so Tab never lands on (and tooltips never open from) something
 *    the clip has hidden.
 *  - MEDIUM: the same shell as full — rail pinned, pane in-flow — except the
 *    pane AUTO-COLLAPSES: its presented state is the ephemeral `openOverlay`
 *    (default closed) instead of the persisted `open`, so content starts
 *    with the width a pinned pane would have consumed. Expanding pins it
 *    until the user (or another action) collapses it — navigation does not.
 *  - COMPACT: no pinned chrome at all. The header, the `moduleStrip` and the
 *    pane live inside a slide-in drawer toggled by {@link AppShellTrigger},
 *    which page-level headers render, so the content area keeps the full
 *    viewport height. The vertical rail does not ride the drawer — the
 *    horizontal strip replaces it there, giving the pane the drawer's full
 *    width.
 *
 * The consuming app owns the pane content (rail tiles, module menus) and the
 * persisted widths — this component is domain-free layout only.
 */
export type AppShellProps = Omittable<{
  /** Full-width bar pinned to the top of the shell. */
  siteHeader?: React.ReactNode;
  /** Fixed narrow far-left navigation column. Full and medium tiers only —
   *  the compact drawer renders `moduleStrip` instead. */
  moduleRail?: React.ReactNode;
  /**
   * Horizontal tier-1 navigation strip for the COMPACT drawer — the rail's
   * stand-in where a vertical column would starve the pane of width. Rendered
   * above the pane inside one shared card, split by a hairline. Never
   * rendered on medium or full.
   */
  moduleStrip?: React.ReactNode;
  /** Width of the module rail column in px. Defaults to 66. */
  moduleRailWidth?: number;
  /** Inset the desktop sidebar from the shell edge when no rail precedes it. */
  insetSidebar?: boolean;
  /**
   * How the navigation region — the rail plus the module-menu pane — is
   * framed. One axis, because the two columns are one region doing one job and
   * a reader should not have to reconcile two props to know how many cards
   * they are looking at.
   *
   *  - `islands` (default): rail and pane are each their own card.
   *  - `joined`: ONE card holds both, split by a vertical hairline. They share
   *    a fill, so the seam is the only thing dividing them.
   *  - `rail-only`: the rail is a card; the pane drops its border, radius and
   *    insets and reads as a region of the shell.
   *  - `with-page`: the rail wears no card at all and sits on the shell
   *    canvas — which this chrome deepens to `sidebar-rail`, one clear step
   *    under the pane's `sidebar`, so rail and gutters read as one plane
   *    distinct from the navigation pane — and the pane joins the CONTENT
   *    card at one shared seam. The page keeps its frame, so a page-owned
   *    background (a canvas, a board, a map) is clipped by the card that
   *    owns it instead of running under a floating column that is not
   *    actually above it.
   *
   * The shell owns this chrome for every slot. A rail that paints its own
   * surface cannot be joined to anything, or unpainted for `with-page`.
   */
  navChrome?: "islands" | "joined" | "rail-only" | "with-page";
  /**
   * Content of the module-menu pane (right of the rail). No chrome is added.
   *
   * The pane's width — and the drag seam that changes it — belong to this
   * content, not to the shell: the pane's root Box carries `resize="right"`
   * and drives `setSidebarWidth` from `useAppShell()`. The shell only reads the
   * resulting width to size the column.
   */
  sidebar?: React.ReactNode;
  /**
   * A detail panel docked to the right of the content as a free-floating
   * island — the mirror of `sidebar` on the far edge. Desktop: while open it
   * is an in-flow column AFTER the content that pushes and shrinks it as it
   * opens (no overlap, no scrim); the island reveals from the right as the
   * column widens. Mobile: a full-screen layer sliding in from the right.
   *
   * The shell owns the island's drag seam: its leading 8px gutter lane is the
   * strip, with the themed indicator centered in the gap, driving the shared
   * `rightPanelWidth` axis. Keep the panel passed through its close
   * transition (toggle `open` rather than dropping it) so the island slides
   * out under the clip instead of vanishing.
   */
  rightPanel?: AppShellIsland;
  /** Main content — rendered inside the inset card and stretched to fill. */
  children: React.ReactNode;
  className?: string;
}>;

/** The right-docked detail island. See {@link AppShellProps.rightPanel}. */
export type AppShellIsland = Omittable<{
  /**
   * The panel. Stays mounted while the island is sliding closed and after,
   * so panel state survives.
   */
  content: React.ReactNode;
  /** Whether the island is presented. */
  open: boolean;
  /** Accessible name for the island's resize handle. Defaults to
   *  "Resize panel". */
  resizeLabel?: string;
}>;

/**
 * Backstop for a dock's settle window: `duration-base` (200ms) plus
 * headroom. Releases the hold when no width `transitionend` arrives — a dock
 * with no box mounted, or a transition the browser dropped.
 */
const PANE_SETTLE_FALLBACK_MS = 350;

/**
 * One docked column's part in the shell's layout motion — the navigation
 * pane, or a right-panel island.
 */
type DockMotionInput = {
  /**
   * The dock's discrete presentation: the pane's or the island's visibility.
   * A change here IS the motion. A drag
   * on a seam changes `extent` without touching it, so streaming widths
   * never engage the pin or the settle window — there the live per-frame
   * reflow is the feedback.
   */
  phase: string | boolean;
  /** The width the dock's clip box is given in this phase — 0 when closed. */
  extent: number;
};

/**
 * The dock open/close motion, from the page's side of the seams.
 *
 * {@link PANE_TRANSITION} animates real `width`, so every frame of a slide
 * relayouts the whole islands row. Two things make that read rough, and this
 * hook owns the countermeasure for both:
 *
 *  - **The content pin.** Left fluid, the page re-wraps text and re-tracks
 *    grids on every frame of the slide. Instead the content column is pinned
 *    to its DESTINATION width for the slide's duration: it lays out exactly
 *    once, at the width it will end on, and the card edge slides across the
 *    finished layout — revealing it when a dock closes, covering it when one
 *    opens.
 *  - **The settle window** (`useLayoutSettling`). Every ResizeObserver-driven
 *    consumer (widget cards, board grids, `ContentSizeProvider`) would
 *    otherwise commit React state and force synchronous layout per frame,
 *    which drops most of the transition's frames. The window tells them to
 *    hold their commits until the motion lands — one reflow at the end.
 *
 * Both open when any dock's `phase` changes (docks that flip in the same
 * commit share one window, and the pin sums their travel) and close together
 * on a clip box's width `transitionend` (attach the returned handler to every
 * dock's clip box), with {@link PANE_SETTLE_FALLBACK_MS} as the backstop.
 * Reduced motion snaps the width with no transition, so neither opens at all.
 */
function useDockMotion({
  docks,
  enabled,
  contentRef,
}: {
  /** Every docked column, in a stable order across renders. */
  docks: readonly DockMotionInput[];
  enabled: boolean;
  /** The content column the pin applies to. */
  contentRef: React.RefObject<HTMLDivElement | null>;
}): {
  onDockTransitionEnd: React.TransitionEventHandler<HTMLElement>;
  /** Fixed content width in px for the slide's duration, else null. */
  pinnedContentWidth: number | null;
} {
  const begin = useBeginLayoutSettle();
  const reducedMotion = usePrefersReducedMotion();
  const [pinnedContentWidth, setPinnedContentWidth] = React.useState<
    number | null
  >(null);
  const releaseRef = React.useRef<(() => void) | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const settle = React.useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    releaseRef.current?.();
    releaseRef.current = null;
    // Unpinning is invisible: the fluid width the content returns to is the
    // width it was pinned at.
    setPinnedContentWidth(null);
  }, []);

  const prevDocksRef = React.useRef<readonly DockMotionInput[] | null>(null);
  // A LAYOUT effect: the pin must land in the same frame a dock's width
  // flips, or the content lays out twice — once fluid, once pinned.
  React.useLayoutEffect(() => {
    const prev = prevDocksRef.current;
    prevDocksRef.current = docks;
    if (!prev) return;
    // The travel of every dock whose phase flipped in this commit: positive
    // when the docks take width from the page, negative when they give it
    // back. A dock's extent may also drift between flips (a drag on its
    // seam); the previous extent is tracked live, so the delta is measured
    // from wherever the dock actually sits.
    let travel = 0;
    let moved = false;
    docks.forEach((dock, index) => {
      const before = prev[index];
      if (!before || before.phase === dock.phase) return;
      moved = true;
      travel += dock.extent - before.extent;
    });
    if (!moved || !enabled || reducedMotion) return;
    // A flip mid-flight restarts the motion: settle the old one first so the
    // counter never double-counts one dock, then re-pin from wherever the
    // slide currently is.
    settle();
    const content = contentRef.current;
    if (content) {
      // The transition has not started at this point, so the rect still
      // reads the slide's starting width; the destination is that less the
      // width the docks take (open) or plus the width they release (close).
      setPinnedContentWidth(
        Math.max(0, Math.round(content.getBoundingClientRect().width - travel)),
      );
    }
    releaseRef.current = begin();
    timerRef.current = setTimeout(settle, PANE_SETTLE_FALLBACK_MS);
    // `docks` is rebuilt every render, so this runs on every commit by
    // design; the phase comparison above is what keeps a commit that moves
    // nothing from touching state.
  }, [docks, enabled, reducedMotion, settle, contentRef, begin]);

  // Unmount closes an in-flight window — a held counter with no owner would
  // freeze every measurement consumer for good.
  React.useEffect(() => settle, [settle]);

  const onDockTransitionEnd = React.useCallback<
    React.TransitionEventHandler<HTMLElement>
  >(
    (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== "width") return;
      settle();
    },
    [settle],
  );

  return { onDockTransitionEnd, pinnedContentWidth };
}

/**
 * The seam hairline the shell draws on the boxes wrapping the pane.
 *
 * `sidebarWidth` is the pane's CONTENT width — the number its own drag seam
 * measures and its `minWidth` clamp is written against — so a wrapper that
 * spends pixels on a border adds them ON TOP of the width, never inside it.
 * Charging the hairline to the pane's budget leaves the pane root short of
 * its own CSS `min-width` at the narrowest drag: it overflows the wrapper by
 * exactly the hairline and its rows paint over the seam, which reads as the
 * border vanishing at minimum width.
 */
const PANE_SEAM = 1;

/**
 * The card the navigation region is drawn on. `borderColor="ink"` is the
 * island edge — an ink veil, deliberately not a hairline token (tuned for
 * separators INSIDE a fill, and lighter than the light rail canvas the cards
 * sit on) — the nav card, the page card and the island all draw the same
 * line around themselves.
 */
function NavFrame({
  children,
  direction = "column",
}: {
  children: React.ReactNode;
  direction?: "row" | "column";
}): React.ReactElement {
  return (
    <Box
      border
      borderColor="ink"
      direction={direction}
      grow
      minHeight={0}
      minWidth={0}
      overflow="hidden"
      rounded="md"
      surface="sidebar"
    >
      {children}
    </Box>
  );
}


export function AppShell({
  siteHeader,
  moduleRail,
  moduleStrip,
  moduleRailWidth = 66,
  insetSidebar = false,
  navChrome = "islands",
  sidebar,
  rightPanel,
  children,
  className,
}: AppShellProps): React.ReactElement {
  const {
    tier,
    isMobile,
    open,
    openMobile,
    setOpenMobile,
    openOverlay,
    sidebarWidth,
    isResizingSidebar,
    rightPanelWidth,
    setRightPanelWidth,
    minRightPanelWidth,
    maxRightPanelWidth,
    isResizingRightPanel,
    setResizingRightPanel,
  } = useAppShell();

  // Medium presents the pane from its own ephemeral state (default closed —
  // the auto-collapse), full from the persisted preference. Same pane, same
  // flow; only the state source differs.
  const paneVisible = (tier === "medium" ? openOverlay : open) && !!sidebar;
  const hasMobileNav = !!moduleStrip || !!sidebar || !!siteHeader;
  const navWithPage = navChrome === "with-page";
  const sidebarStartInset = insetSidebar && !moduleRail;
  // Joining the pane to the page is the whole point of `with-page`. A joined
  // pane renders INSIDE the content card — one card, one border, one shadow — so
  // the island's chrome genuinely wraps both columns instead of two adjacent
  // cards faking a seam with squared-off corners. The pane stays mounted in
  // there while collapsed (clipped to zero width) so the slide transition
  // never reparents it. Not on compact: the drawer holds the pane itself.
  const paneInCard = !isMobile && !!sidebar && navWithPage;
  const navJoined = navChrome === "joined" && !!moduleRail;
  const flushSidebar = navChrome === "rail-only";
  // A free-standing pane card borders BOTH vertical edges; a flush pane
  // borders neither. Either way the chrome rides on top of `sidebarWidth` —
  // see {@link PANE_SEAM}.
  const paneCardChrome = flushSidebar ? 0 : 2 * PANE_SEAM;

  // The dock motion — the content pin and the settle window — around every
  // width transition the shell runs: the pane's (closed by whichever of the
  // three pane clip boxes this chrome mounts) and the island's. The pane's
  // extent restates its chrome's own clip width so the pin's delta equals the
  // travel. Compact has no collapse (the pane is a drawer) and mobile islands
  // are transform layers, so the motion never engages there.
  const contentInnerRef = React.useRef<HTMLDivElement>(null);
  const paneFullWidth =
    paneInCard || navJoined
      ? sidebarWidth + PANE_SEAM
      : sidebarWidth + paneCardChrome + 8 + (sidebarStartInset ? 8 : 0);

  // The page card and the islands row — page card plus the docked island —
  // that together bound the region published as
  // {@link APP_SHELL_ISLANDS_INSET_VARS}. Only desktop mounts the row; on
  // mobile the card alone is the region.
  const cardRef = React.useRef<HTMLDivElement>(null);
  const islandsRowRef = React.useRef<HTMLDivElement>(null);
  usePublishIslandsInsets(cardRef, islandsRowRef, isMobile);
  // The width the island's column presents at — its card plus the 8px
  // gutter lane it carries on its leading edge.
  const islandPresentedWidth = rightPanelWidth + ISLAND_HANDLE_LANE;
  const islandOpen = !!rightPanel?.open;

  const { onDockTransitionEnd, pinnedContentWidth } = useDockMotion({
    docks: [
      { phase: paneVisible, extent: paneVisible ? paneFullWidth : 0 },
      { phase: islandOpen, extent: islandOpen ? islandPresentedWidth : 0 },
    ],
    enabled: !isMobile,
    contentRef: contentInnerRef,
  });

  const header = siteHeader ? (
    // Positioned + z-index so header-anchored menus and tooltips paint over
    // page content below.
    <Box position="relative" shrink={false} zIndex={40}>
      {siteHeader}
    </Box>
  ) : null;

  // With a header above, the columns start at the row top and the header
  // supplies the gutter over them. With no header they ARE the top of the
  // shell, so each island column pays the 8px inset it keeps on every other
  // edge — a flush sidebar opts out, which is what makes it read as shell.
  const topInset = !isMobile && !header;

  const content = (
    <ContentPane
      inset={!isMobile}
      insetTop={topInset}
      // The rail or a free-standing pane column owns the gutter before the
      // page island, so the content column does not add a second inset on
      // that side. A pane joined INTO the card supplies no gutter — the card
      // itself pays the inset.
      cardRef={cardRef}
      flushLeft={!!moduleRail || (paneVisible && !paneInCard)}
      innerRef={contentInnerRef}
      pinnedWidth={pinnedContentWidth}
      pane={
        paneInCard ? (
          // Outer clips and animates; inner keeps its full width so
          // collapsing slides the pane out from beneath the card's edge
          // rather than reflowing it. The card's own overflow clip rounds
          // the pane's outer corners.
          <Box
            className={isResizingSidebar ? undefined : PANE_TRANSITION}
            inert={!paneVisible || undefined}
            onTransitionEnd={onDockTransitionEnd}
            overflow="hidden"
            position="relative"
            shrink={false}
            width={paneVisible ? sidebarWidth + PANE_SEAM : 0}
            zIndex={10}
          >
            <Box
              // The seam INSIDE the card: the pane's own hairline is the
              // only thing dividing it from the page.
              border="right"
              borderColor="border"
              direction="column"
              height="100%"
              shrink={false}
              surface="sidebar"
              width={sidebarWidth + PANE_SEAM}
            >
              {sidebar}
            </Box>
          </Box>
        ) : undefined
      }
    >
      {children}
    </ContentPane>
  );

  if (isMobile) {
    return (
      <Box
        background="sunken"
        // Same canvas rule as desktop — the fill barely shows on mobile (the
        // content pane runs flush), but the drawer's gutters read from it.
        className={cn(navWithPage && "bg-sidebar-rail", className)}
        direction="column"
        fill
      >
        <Box direction="column" fill overflow="hidden" position="relative">
          {content}
          {rightPanel && <AppShellMobileIslandLayer island={rightPanel} />}
        </Box>
        {hasMobileNav && (
          <Sheet onOpenChange={setOpenMobile} open={openMobile}>
            <SheetPopup
              // Flush to the left edge, full height, rounded away from it —
              // the drawer IS the navigation surface, not a card floating in
              // a sheet. `sidebar` keeps it the same chrome fill the desktop
              // frame paints, instead of lifting off the page only on mobile.
              className="w-[86dvw] max-w-none rounded-e-2xl"
              // A nav drawer opening must not pull focus onto the first nav
              // tile; focus rests on the popup and the user tabs in.
              initialFocus={false}
              showCloseButton={false}
              side="left"
              surface="sidebar"
            >
              {/* ONE surface holds the whole navigation, top to bottom: the
                  site header (workspace switcher / search / account present
                  WITH the navigation instead of spending a pinned header
                  row), the horizontal module strip, then the pane under a
                  hairline. The vertical rail never rides the drawer — a rail
                  column would spend ~56px of an already-narrow overlay
                  restating what the strip says in one row, and the pane pays
                  for it in truncated labels. */}
              <Box direction="column" fill overflow="hidden">
                {siteHeader && (
                  <Box direction="column" shrink={false}>
                    {siteHeader}
                  </Box>
                )}
                {moduleStrip && (
                  <Box
                    border="bottom"
                    borderColor="border"
                    direction="column"
                    shrink={false}
                  >
                    {moduleStrip}
                  </Box>
                )}
                {sidebar && (
                  <Box direction="column" grow minHeight={0} minWidth={0}>
                    {sidebar}
                  </Box>
                )}
              </Box>
              {/* The drawer's close verb rides OUTSIDE the drawer, over the
                  scrim strip the drawer leaves on the right, on the header's
                  row (8px in, the 28px rung — level with the search button).
                  A tap on the scrim closes the drawer too, but nothing says
                  so; a visible × does. It sits over the scrim in both
                  themes, so it paints its own dark plate under light ink
                  (the tooltip's `dark` mechanism) instead of borrowing the
                  page's — on a light page the scrim is mid-grey. `start-full`
                  lands on the padding edge, so the margin counts the drawer's
                  1px seam to keep the 8px gap true. */}
              <SheetClose
                aria-label="Close navigation"
                className="dark absolute start-full top-2 ms-[calc(--spacing(2)+1px)] bg-black/40 hover:bg-black/56 data-pressed:bg-black/64"
                data-slot="app-shell-drawer-close"
                render={<Button size="icon-sm" variant="ghost" />}
              >
                <Icon name="x" size="md" />
              </SheetClose>
            </SheetPopup>
          </Sheet>
        )}
      </Box>
    );
  }

  return (
    <Box
      // The shell canvas. `with-page` deepens it to `sidebar-rail` — one
      // clear step under the pane's `sidebar` — so the bare rail and every
      // gutter around the islands read as one plane the cards float on,
      // visibly distinct from the navigation pane (Slack's arrangement).
      // The card chromes keep `sunken`, where the nav fills carry the split.
      background="sunken"
      className={cn(navWithPage && "bg-sidebar-rail", className)}
      direction="column"
      fill
    >
      {header}
      {/* The row owns the trailing screen-edge gutter (the content card pays
          no right padding of its own): an island column can then butt
          directly against the page card and its own 8px leading lane IS the
          gap between them — which is what lets the island's resize seam span
          that gap with its indicator centered in it. */}
      <Box
        direction="row"
        fill
        overflow="hidden"
        paddingRight="sm"
        position="relative"
      >
        {navJoined ? (
          // One card, two columns, one hairline. The insets belong to the
          // wrapper so the card itself can stay a plain frame.
          <Box
            direction="column"
            height="100%"
            marginX="sm"
            paddingBottom="sm"
            paddingTop={topInset ? "sm" : undefined}
            position="relative"
            shrink={false}
            zIndex={10}
          >
            <NavFrame direction="row">
              <Box direction="column" shrink={false} width={moduleRailWidth}>
                {moduleRail}
              </Box>
              {sidebar && (
                // Same clip-and-hold as the split pane: the outer column
                // animates to zero while the inner keeps its width, so the
                // pane slides out from under the card's edge.
                <Box
                  className={isResizingSidebar ? undefined : PANE_TRANSITION}
                  height="100%"
                  inert={!paneVisible || undefined}
                  onTransitionEnd={onDockTransitionEnd}
                  overflow="hidden"
                  shrink={false}
                  width={paneVisible ? sidebarWidth + PANE_SEAM : 0}
                >
                  <Box
                    // The seam INSIDE the card. Rail and pane share the card's
                    // fill, so this line is the only thing dividing them.
                    border="left"
                    borderColor="border"
                    direction="column"
                    height="100%"
                    shrink={false}
                    width={sidebarWidth + PANE_SEAM}
                  >
                    {sidebar}
                  </Box>
                </Box>
              )}
            </NavFrame>
          </Box>
        ) : (
          <>
            {moduleRail && (
              <Box
                // The wash ladder shifts one rung on the bare rail, because
                // the ground under it does. `with-page` seats these chips on
                // `sidebar-rail`, a clear step below every surface the ladder
                // was tuned against, and an alpha veil buys less lightness the
                // deeper it composites: the 5% hover rung moves this ground
                // ~3 points of L in light mode, against ~5 on a card. Hover
                // takes the selected rung's value and press takes the rung
                // above it, so the two states stay one step apart and both
                // stay veils of the theme's own ink — a literal here would go
                // deaf to a custom seed. Only the bare rail remaps them: in
                // every card chrome this same column wears a `NavFrame`, which
                // seats the chips on `sidebar` like any other surface, and the
                // tuned ladder is already right there.
                className={
                  navWithPage
                    ? "[--wash-active:var(--wash-selected-hover)] [--wash-hover:var(--wash-selected)]"
                    : undefined
                }
                direction="column"
                height="100%"
                // Symmetric, in every chrome. The rail's items are centered in
                // this column, so an uneven margin lands entirely on one side
                // of them: dropping only the left one (which a bare rail can
                // seem to invite, since it is the shell's own edge) put the
                // chips 12px from the viewport and 20px from the page card.
                marginX="sm"
                paddingBottom="sm"
                paddingTop={topInset ? "sm" : undefined}
                position="relative"
                shrink={false}
                width={moduleRailWidth}
                zIndex={10}
              >
                {/* `with-page` gives the rail no card: it sits directly on the
                    shell canvas — the deeper `sidebar-rail` fill in this
                    chrome — which is what makes the page card (pane included)
                    the one object floating on it. */}
                {navWithPage ? moduleRail : <NavFrame>{moduleRail}</NavFrame>}
              </Box>
            )}
        {sidebar && !paneInCard && (
          // Outer clips; inner keeps its full width so collapsing slides the
          // pane out from beneath the clip rather than reflowing it.
          <Box
            // No easing while a seam is dragging — the pane must track the
            // pointer exactly, not ease toward each streamed width.
            className={isResizingSidebar ? undefined : PANE_TRANSITION}
            height="100%"
            inert={!paneVisible || undefined}
            onTransitionEnd={onDockTransitionEnd}
            overflow="hidden"
            position="relative"
            // The pane is a fixed-width column, not a shrinkable one. Without
            // this it shares the row's overflow with the content column: a page
            // whose min-content width exceeds the row (a wide board or table)
            // squeezes the pane down and its own content spills into a
            // horizontal scrollbar. `shrink={false}` pins the pane and leaves
            // the content column — which has `minWidth={0}` and clips inside —
            // to absorb the overflow, exactly like the rail beside it.
            shrink={false}
            width={
              paneVisible
                ? sidebarWidth + paneCardChrome + 8 + (sidebarStartInset ? 8 : 0)
                : 0
            }
            zIndex={10}
          >
            <Box
              direction="column"
              height="100%"
              marginLeft={sidebarStartInset ? "sm" : undefined}
              paddingBottom={flushSidebar ? undefined : "sm"}
              paddingTop={topInset && !flushSidebar ? "sm" : undefined}
              shrink={false}
              width={sidebarWidth + paneCardChrome}
            >
              <Box
                border={!flushSidebar}
                // Naming a color is enough to draw a hairline (Box treats it
                // as intent), so a flush pane must leave this unset too.
                borderColor={flushSidebar ? undefined : "border"}
                direction="column"
                grow
                minHeight={0}
                overflow="hidden"
                // A flush pane's FILL runs to the shell's top edge — that is
                // what makes it read as shell — but its content still lines up
                // with the island columns beside it, so the gutter moves
                // inside the surface instead of above it.
                paddingTop={flushSidebar && topInset ? "sm" : undefined}
                rounded={flushSidebar ? undefined : "md"}
                surface="sidebar"
              >
                {sidebar}
              </Box>
            </Box>
          </Box>
        )}
          </>
        )}
        {/* The islands row: the page card and the docked island, and
            nothing of the navigation. Its right edge is the region's — the
            open island's card, or the page card's own edge when it is
            closed — so a right-anchored toast opens over the island instead
            of being pushed off it, and it holds still while the island docks. */}
        <Box
          data-slot="app-shell-islands"
          direction="row"
          grow
          height="100%"
          minWidth={0}
          ref={islandsRowRef}
        >
          <Box direction="column" grow height="100%" minWidth={0}>
            {content}
          </Box>
          {rightPanel && (
            <AppShellDockedIsland
              island={rightPanel}
              maxWidth={maxRightPanelWidth}
              minWidth={minRightPanelWidth}
              onResizingChange={setResizingRightPanel}
              onTransitionEnd={onDockTransitionEnd}
              onWidthChange={setRightPanelWidth}
              presentedWidth={islandPresentedWidth}
              resizing={isResizingRightPanel}
              topInset={topInset}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Content column. On desktop it renders as a free-standing inset card
 * (bordered, rounded, page-background); on mobile it fills flush. Children are
 * wrapped in a filling Box so a page's own `fill` resolves against a definite
 * height. The shell measures the card for the islands region it publishes on
 * the root — see {@link APP_SHELL_ISLANDS_INSET_VARS}.
 */
function ContentPane({
  inset,
  insetTop,
  cardRef,
  children,
  flushLeft,
  innerRef,
  pane,
  pinnedWidth,
}: Omittable<{
  inset?: boolean;
  /** Pay the top gutter too — for a shell with no header above the row. */
  insetTop?: boolean;
  /** The card element, for the shell's region measurement. */
  cardRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
  flushLeft?: boolean;
  /** The content column, for the pane motion's pin measurement. */
  innerRef?: React.Ref<HTMLDivElement>;
  /**
   * A navigation pane joined into this card as its first column. The card's
   * border, radius and shadow wrap pane and page together — one island —
   * and the pane's own hairline is the seam between them.
   */
  pane?: React.ReactNode;
  /**
   * Fixed content width in px while the pane slides — the destination width,
   * so the page lays out once and the card edge travels across the finished
   * layout (clipping the overflow on collapse, leaving the card's own fill on
   * expand). Null between slides: the column is fluid again at the width it
   * was pinned at, so unpinning repaints nothing.
   */
  pinnedWidth?: number | null;
}>): React.ReactElement {
  return (
    <Box
      direction="column"
      fill
      // With a header, no top padding: rail and pane start at the row top, so
      // the card's top edge lines up with theirs. Bottom padding matches their
      // 8px inset.
      paddingBottom={inset ? "sm" : undefined}
      paddingTop={inset && insetTop ? "sm" : undefined}
      paddingLeft={inset && !flushLeft ? "sm" : undefined}
      // No right padding: the desktop row pays the screen-edge gutter, and an
      // open island brings its own leading lane — a padding here would double
      // the gap between the page and the island.
    >
      <Box
        overflow="hidden"
        // The stable anchor overlays pin to: a sidebar-style sheet insets
        // itself to this card's rect (top/right/bottom + radius) so it reads
        // as a panel INSIDE the content card, not a full-viewport layer.
        id="__dialog-anchor"
        data-slot="app-shell-content"
        ref={cardRef}
        // `background` — the content column IS the page. The gutter around it
        // and the navigation either side of it are the `sunken` frame, and
        // everything the column hosts stacks upward from here: panels are
        // `card`, overlays are `popover`.
        //
        // `border` supplies the WIDTH; `borderColor` the line. The surface's
        // own hairline (`--sunken-border`, resolved on the shell canvas) is
        // LIGHTER than the light `sidebar-rail` gutter, so it vanished there;
        // the ink veil composites darker than any ground. `--background-shadow`
        // is a soft drop with no ring layer, so without this line the card has
        // no edge at all against the gutter.
        border={inset}
        borderColor={inset ? "ink" : undefined}
        // The fill is the PAGE'S identity and holds at every viewport — only
        // the card chrome (inset, border, radius) drops on mobile. Gating the
        // surface on `inset` made the page silently change color at the
        // mobile breakpoint (the shell's sunken showed through).
        surface="background"
        direction={pane ? "row" : "column"}
        fill
        rounded={inset ? "md" : undefined}
      >
        {pane}
        {/* The page is the shell's one `main` landmark: a screen reader
            jumps past the navigation straight to it. */}
        <Box
          as="main"
          direction="column"
          grow={pinnedWidth == null}
          minHeight={0}
          minWidth={pinnedWidth == null ? 0 : undefined}
          ref={innerRef}
          shrink={pinnedWidth == null ? undefined : false}
          width={pinnedWidth ?? undefined}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
